import { createFileRoute, Link } from "@tanstack/react-router";
import {
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle2,
  Mail,
  Phone,
  ChevronRight,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { VACANCIES } from "@/data/vacancies";
import careers640 from "@/assets/careers-640.webp.asset.json";
import careers960 from "@/assets/careers-960.webp.asset.json";
import careers1400 from "@/assets/careers-1400.webp.asset.json";

const SITE_URL = "https://elvion-group.nl";

const USP_ITEMS = [
  "Salaris conform CAO Metaal & Techniek",
  "Lease-auto ook voor privégebruik (monteurs)",
  "Opleidingsbudget en doorgroeimogelijkheden",
  "Hybride werken waar mogelijk",
  "Pensioen via PME",
  "25 vakantiedagen + 13 ADV-dagen",
  "Persoonlijk ontwikkelplan",
  "Korte lijnen en informele sfeer",
];

export const Route = createFileRoute("/werken-bij/")({
  head: () => {
    const url = `${SITE_URL}/werken-bij`;
    const title = "Werken bij Elvion Group Nederland | Vacatures elektrotechniek & beveiliging";
    const description =
      "Bekijk vacatures bij Elvion Group Nederland: installatiemonteur, werkvoorbereider en binnendienst. Goede arbeidsvoorwaarden en doorgroeimogelijkheden.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
        { property: "og:site_name", content: "Elvion Group Nederland" },
        { property: "twitter:card", content: "summary_large_image" },
        { property: "twitter:title", content: title },
        { property: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                  { "@type": "ListItem", position: 2, name: "Werken bij ons", item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: WerkenBijPage,
});

function WerkenBijPage() {
  return (
    <>
      <Header variant="shop" />
      {/* Banner */}
      <div className="w-full bg-white">
        <div className="mx-auto max-w-7xl px-4 py-3.5 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
          <div className="grid items-center gap-6 overflow-hidden rounded-2xl border border-border bg-card shadow-sm lg:grid-cols-2 lg:gap-0">
            <div className="relative h-[200px] w-full overflow-hidden sm:h-[260px] lg:h-full lg:min-h-[340px]">
              <img
                src={careers960.url}
                srcSet={`${careers640.url} 640w, ${careers960.url} 960w, ${careers1400.url} 1400w`}
                sizes="(min-width: 1024px) 50vw, 100vw"
                alt="Monteur Elvion Group Nederland met veiligheidshelm"
                className="absolute inset-0 h-full w-full object-cover object-top sm:object-top lg:object-center"
                width={1200}
                height={794}
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="flex flex-col justify-center p-6 sm:p-10 lg:p-12">
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                Ben jij klaar voor deze uitdaging?
              </h1>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-text-secondary sm:text-lg">
                Ik bouw elke dag aan een betere versie van mezelf, mijn team en ons bedrijf. Bij Elvion Group Nederland
                investeren we in mensen die willen groeien, samen winnen en elke dag net dat stapje harder zetten.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Salaris conform CAO Metaal & Techniek",
                  "Opleidingsbudget en doorgroeimogelijkheden",
                  "25 vakantiedagen + 13 ADV-dagen",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-text-secondary sm:text-base">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <main className="bg-white">
        {/* Vacatures */}
        <section id="vacatures" className="bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 py-9 sm:px-6 lg:px-8 lg:py-12">

            <div className="grid gap-6 lg:grid-cols-3">
              {VACANCIES.map((vacancy) => (
                <div
                  key={vacancy.title}
                  className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                      <vacancy.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-orange">{vacancy.department}</p>
                      <h3 className="font-display text-lg font-bold text-foreground">
                        {vacancy.title}
                      </h3>
                    </div>
                  </div>

                  <div className="mb-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {vacancy.location}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {vacancy.type}
                    </span>
                  </div>

                  <p className="mb-4 text-sm leading-relaxed text-text-secondary">
                    {vacancy.shortDescription}
                  </p>

                  <div className="mb-6">
                    <h4 className="mb-2 text-sm font-semibold text-foreground">Wij vragen o.a.</h4>
                    <ul className="space-y-1.5">
                      {vacancy.requirements.slice(0, 3).map((req) => (
                        <li key={req} className="flex items-start gap-2 text-sm text-text-secondary">
                          <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto pt-4 border-t border-border">
                    <Link
                      to="/werken-bij/$slug"
                      params={{ slug: vacancy.slug }}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Bekijk vacature
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>

                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-primary-foreground sm:p-12">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-orange/20 blur-3xl" />
            <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-orange/10 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-2xl font-extrabold sm:text-3xl">
                Geen passende vacature?
              </h2>
              <p className="mt-3 max-w-xl text-primary-foreground/80">
                Wij zijn altijd op zoek naar enthousiaste vakmensen in de elektrotechniek, beveiliging
                en installatietechniek. Stuur een open sollicitatie en we nemen contact met je op.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="mailto:werkenbij@elvion-group.nl"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  <Mail className="h-4 w-4" />
                  Open sollicitatie sturen
                </a>
                <a
                  href="tel:+31850806695"
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/20"
                >
                  <Phone className="h-4 w-4" />
                  085 080 66 95
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
